import { defineStore } from 'pinia'
import { reactive } from 'vue'
import type { ITrustpilotRatingModel } from '@/modules/adminModule/types/trustpilotRating'

interface IStorePromises {
	fetchTrustpilotRatingsPromise: Promise<ITrustpilotRatingModel> | null
}

export interface ITrustpilotRatingsStoreState {
	trustpilotRating: ITrustpilotRatingModel | null
	fetchingTrustpilotRating: boolean
}

export interface ITrustpilotRatingsStore {
	readonly state: ITrustpilotRatingsStoreState
	fetchTrustpilotRating: () => Promise<ITrustpilotRatingModel | undefined>
	setTrustpilotRating: (rating: ITrustpilotRatingModel) => void
	setFetchingTrustpilotRating: (fetching: boolean) => void
	getTrustpilotRating: ComputedRef<ITrustpilotRatingModel | null>
	getFetchingTrustpilotRating: ComputedRef<boolean>
}

export const useTrustpilotRatingsStore = defineStore('trustpilotRatingsStore', (): ITrustpilotRatingsStore => {
	const _state = reactive<ITrustpilotRatingsStoreState>({
		trustpilotRating: null,
		fetchingTrustpilotRating: false,
	})

	const setTrustpilotRating = (rating: ITrustpilotRatingModel) => {
		_state.trustpilotRating = rating
	}

	const promises: IStorePromises = {
		fetchTrustpilotRatingsPromise: null,
	}

	const fetchTrustpilotRating = async () => {
		try {
			if (_state.trustpilotRating) {
				return _state.trustpilotRating
			}

			if (promises.fetchTrustpilotRatingsPromise) {
				return promises.fetchTrustpilotRatingsPromise
			}

			promises.fetchTrustpilotRatingsPromise = (async () => {
				const fetched = await ApiService.fetchTrustpilotRating()

				if (fetched) {
					_state.trustpilotRating = fetched
				} else {
					_state.trustpilotRating = null
				}

				return _state.trustpilotRating as ITrustpilotRatingModel
			})()

			return await promises.fetchTrustpilotRatingsPromise
		} catch (error) {
			logger.log(error)
		}
	}

	const setFetchingTrustpilotRating = (fetching: boolean) => {
		_state.fetchingTrustpilotRating = fetching
	}

	const getTrustpilotRating = computed(() => _state.trustpilotRating)
	const getFetchingTrustpilotRating = computed(() => _state.fetchingTrustpilotRating)

	return {
		state: _state,
		setTrustpilotRating,
		fetchTrustpilotRating,
		setFetchingTrustpilotRating,
		getTrustpilotRating,
		getFetchingTrustpilotRating,
	}
})
